import { useContext, useState } from 'react';
import { Table, Tag, Select, Modal, Button, Space, Badge, Tooltip, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from '../../config/axios';
import dayjs from 'dayjs';
import AppLayout from '../../layouts/app.layout';
import { AuthContext } from '../../context/auth.context';
import { handleAPIError } from '../../utils/helpers';

type StaffType = 'support' | 'support_admin' | 'insurance' | null;

interface AdminUser {
	id: number;
	first_name: string;
	last_name: string;
	phone_number: string;
	email: string;
	is_superuser: boolean;
	is_active: boolean;
	staff_type: StaffType;
	created_at: string;
}

const ACCESS_LEVELS: { value: StaffType; label: string; color: string }[] = [
	{ value: null, label: 'Admin', color: 'blue' },
	{ value: 'support_admin', label: 'Support Admin', color: 'purple' },
	{ value: 'support', label: 'Support', color: 'cyan' },
	{ value: 'insurance', label: 'Insurance', color: 'green' },
];

function accessLabel(user: AdminUser) {
	if (user.is_superuser) return { label: 'Super Admin', color: 'gold' };
	return ACCESS_LEVELS.find((l) => l.value === user.staff_type) ?? { label: 'Admin', color: 'blue' };
}

export default function ManageAdmins() {
	const { user: currentUser } = useContext(AuthContext);
	const queryClient = useQueryClient();

	const [editTarget, setEditTarget] = useState<AdminUser | null>(null);
	const [selectedLevel, setSelectedLevel] = useState<StaffType>(null);

	const { data: admins = [], isLoading, error } = useQuery<AdminUser[]>({
		queryKey: ['admin-users'],
		queryFn: async () => {
			const res = await axios.get('/admin/super/admins', { withCredentials: true });
			return res.data.results;
		},
	});
	if (error) handleAPIError(error);

	const updateAccess = useMutation({
		mutationFn: async ({ id, staff_type }: { id: number; staff_type: StaffType }) => {
			await axios.patch(`/admin/users/${id}`, { staff_type }, { withCredentials: true });
		},
		onSuccess: () => {
			message.success('Access level updated');
			queryClient.invalidateQueries({ queryKey: ['admin-users'] });
			setEditTarget(null);
		},
		onError: handleAPIError,
	});

	const toggleActive = useMutation({
		mutationFn: async ({ id, is_active }: { id: number; is_active: boolean }) => {
			await axios.patch(`/admin/users/${id}`, { is_active }, { withCredentials: true });
		},
		onSuccess: () => {
			message.success('User status updated');
			queryClient.invalidateQueries({ queryKey: ['admin-users'] });
		},
		onError: handleAPIError,
	});

	const openEdit = (admin: AdminUser) => {
		setSelectedLevel(admin.staff_type);
		setEditTarget(admin);
	};

	const columns = [
		{
			title: 'Name',
			key: 'name',
			render: (_: unknown, r: AdminUser) => (
				<Space direction="vertical" size={0}>
					<span style={{ fontWeight: 500 }}>
						{r.first_name} {r.last_name}
						{r.id === currentUser?.id && (
							<Tag color="default" style={{ marginLeft: 6, fontSize: 10 }}>You</Tag>
						)}
					</span>
					<span style={{ fontSize: 12, color: '#888' }}>{r.phone_number}</span>
				</Space>
			),
		},
		{
			title: 'Email',
			dataIndex: 'email',
			key: 'email',
			render: (v: string) => <span style={{ fontSize: 13 }}>{v || '—'}</span>,
		},
		{
			title: 'Access Level',
			key: 'access',
			render: (_: unknown, r: AdminUser) => {
				const { label, color } = accessLabel(r);
				return <Tag color={color}>{label}</Tag>;
			},
		},
		{
			title: 'Status',
			key: 'status',
			render: (_: unknown, r: AdminUser) => (
				<Badge
					status={r.is_active ? 'success' : 'default'}
					text={r.is_active ? 'Active' : 'Inactive'}
				/>
			),
		},
		{
			title: 'Joined',
			dataIndex: 'created_at',
			key: 'created_at',
			render: (v: string) => dayjs(v).format('DD MMM YYYY'),
		},
		{
			title: 'Actions',
			key: 'actions',
			render: (_: unknown, r: AdminUser) => {
				const isSelf = r.id === currentUser?.id;
				const isSuperUser = r.is_superuser;
				return (
					<Space>
						<Tooltip title={isSuperUser ? 'Cannot change Super Admin access' : isSelf ? 'Cannot change your own access' : ''}>
							<Button
								size="small"
								disabled={isSelf || isSuperUser}
								onClick={() => openEdit(r)}
							>
								Change Access
							</Button>
						</Tooltip>
						<Tooltip title={isSelf ? 'Cannot deactivate yourself' : ''}>
							<Button
								size="small"
								danger={r.is_active}
								disabled={isSelf}
								loading={toggleActive.isPending}
								onClick={() =>
									Modal.confirm({
										title: r.is_active ? 'Deactivate user?' : 'Activate user?',
										content: `${r.first_name} ${r.last_name} will ${r.is_active ? 'lose' : 'regain'} access to the backoffice.`,
										okText: r.is_active ? 'Deactivate' : 'Activate',
										okButtonProps: { danger: r.is_active },
										onOk: () => toggleActive.mutate({ id: r.id, is_active: !r.is_active }),
									})
								}
							>
								{r.is_active ? 'Deactivate' : 'Activate'}
							</Button>
						</Tooltip>
					</Space>
				);
			},
		},
	];

	return (
		<AppLayout title="Manage Admins" subtitle="View and manage admin user access levels">
			<Table
				dataSource={admins}
				columns={columns}
				rowKey="id"
				loading={isLoading}
				pagination={false}
				size="small"
				rowClassName={(r) => (!r.is_active ? 'opacity-50' : '')}
			/>

			<Modal
				open={!!editTarget}
				title={`Change Access — ${editTarget?.first_name} ${editTarget?.last_name}`}
				onCancel={() => setEditTarget(null)}
				onOk={() => updateAccess.mutate({ id: editTarget!.id, staff_type: selectedLevel })}
				confirmLoading={updateAccess.isPending}
				okText="Save"
			>
				<p style={{ color: '#666', marginBottom: 16, fontSize: 13 }}>
					Select the new access level for this user.
				</p>
				<Select
					style={{ width: '100%' }}
					value={selectedLevel}
					onChange={(v) => setSelectedLevel(v)}
					options={ACCESS_LEVELS.map((l) => ({ value: l.value, label: l.label }))}
				/>
			</Modal>
		</AppLayout>
	);
}
